import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import HNLNavLogo from "@/assets/certificates/hnl-logo.png";
import MagnetProgramIcon from "@/assets/icons/magnet-program-icon.png";
import HomeCampusIcon from "@/assets/icons/home-campus-icon.png";
import PreferredIdIcon from "@/assets/icons/preferred-id-icon.png";

// Import fonts
// @ts-ignore
import ProximaNovaReg from "@/assets/fonts/proximanova_regular.ttf";
// @ts-ignore
import ProximaNovaBold from "@/assets/fonts/proximanova_bold.otf";

// Register fonts
Font.register({ family: "ProximaNova", src: ProximaNovaReg });
Font.register({
  family: "ProximaNovaBold",
  src: ProximaNovaBold,
  fontWeight: "bold",
});

// Styles based on the Figma design
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    fontFamily: "ProximaNova",
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  pageContent: {
    paddingHorizontal: 24,
    paddingBottom: 40, // Space for footer
  },

  // Light bar at top
  lightBar: {
    height: 6,
    backgroundColor: "#FFD100",
    width: "100%",
  },

  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 6,
    paddingBottom: 0,
    paddingHorizontal: 0,
  },
  logo: {
    width: 80,
    height: 15,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  studentIdContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  studentIdIcon: {
    width: 16,
    height: 16,
  },
  studentIdLabel: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },
  studentIdValue: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },

  // Student Name Section
  studentNameSection: {
    alignItems: "center",
    paddingTop: 0,
    paddingBottom: 0,
    gap: 2,
  },
  badgeContainer: {
    backgroundColor: "#1c252e",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    marginBottom: 2,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#ffffff",
  },
  studentName: {
    fontSize: 32,
    fontFamily: "ProximaNovaBold",
    fontWeight: "bold",
    color: "#1c252e",
    marginBottom: 0,
  },
  currentSchoolContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  currentSchoolLabel: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    marginBottom: 8,
  },
  currentSchool: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    marginBottom: 8,
  },

  // Info Boxes (Program, CTE, Pathway)
  infoSection: {
    backgroundColor: "#f4f6f8",
    borderRadius: 8,
    padding: 8,
    marginBottom: 8,
    gap: 8,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  infoLabel: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
    textTransform: "uppercase",
  },
  infoValueContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  infoIcon: {
    width: 16,
    height: 16,
  },
  infoValue: {
    fontSize: 10,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },

  // Grade Headers
  gradeHeaderRow: {
    flexDirection: "row",
    gap: 8,
    marginTop: 4,
    marginBottom: 8,
  },
  gradeHeader: {
    flex: 1,
    backgroundColor: "rgba(142, 51, 255, 0.12)",
    borderRadius: 8,
    paddingVertical: 2,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  gradeHeaderText: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
  },

  // Course Section
  courseSection: {
    marginBottom: 0,
  },
  courseSectionTitle: {
    fontSize: 13,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
    marginBottom: 8,
  },
  sequenceContainer: {
    marginBottom: 12,
  },
  sequenceHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  courseDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  sequenceName: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
  },
  sequenceLabelChip: {
    backgroundColor: "#D6F4F9",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    minHeight: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  sequenceLabelChipText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#006C9C",
  },
  courseTag: {
    backgroundColor: "rgba(0, 184, 217, 0.16)",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    minHeight: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  courseTagText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#006c9c",
  },
  courseRow: {
    flexDirection: "row",
    gap: 8,
  },
  courseCell: {
    flex: 1,
    backgroundColor: "#f4f6f8",
    borderRadius: 8,
    padding: 8,
    height: 88,
    justifyContent: "space-between",
    overflow: "hidden",
  },
  courseName: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    lineHeight: 1.6,
    textAlign: "center",
  },
  courseCode: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#637381",
    textAlign: "center",
  },
  courseCodeContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: 24, // Fixed height to match badge
    flexShrink: 0,
  },
  courseNameContainer: {
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "center",
    flexShrink: 1,
  },
  courseNameWithMore: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    marginBottom: 4,
    lineHeight: 1.6,
    textAlign: "center",
  },
  courseMoreText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#637381",
    textAlign: "center",
  },
  plusBadge: {
    backgroundColor: "rgba(145, 158, 171, 0.16)",
    paddingHorizontal: 6,
    paddingVertical: 0,
    borderRadius: 6,
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  plusBadgeText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#637381",
  },
  courseCodeWithBadge: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    height: 24, // Fixed height to match single course code container
    flexShrink: 0,
  },
  courseCodeText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#637381",
  },

  // Electives Section
  electivesSection: {
    marginTop: 0,
  },
  electivesHeader: {
    fontSize: 13,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
    marginBottom: 4,
  },
  electivesSubheader: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    marginBottom: 8,
  },
  electiveCategory: {
    marginBottom: 8,
  },
  electiveCategoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  electiveCategoryDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  electiveCategoryName: {
    fontSize: 10,
    fontFamily: "ProximaNovaBold",
    color: "#1c252e",
  },
  electiveCard: {
    backgroundColor: "rgba(255, 171, 0, 0.08)",
    borderRadius: 8,
    padding: 8,
    alignItems: "center",
  },
  electiveCourseName: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
    textAlign: "center",
  },

  // Footer
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#ededed",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 8,
    height: 32,
  },
  footerText: {
    fontSize: 9,
    fontFamily: "ProximaNova",
    color: "#1c252e",
  },
});

interface CoursePlanPDFProps {
  data: CoursePlanWithClimber; // Course plan data
}

export const CoursePlanPDF: React.FC<CoursePlanPDFProps> = ({ data }) => {
  // Color configuration based on sequence type (same as Step6Summary)
  const TYPE_COLORS: Record<
    "CTE" | "CORE" | "ELECTIVE",
    { dotColor: string; cardColor: string }
  > = {
    CTE: {
      dotColor: "#9c6ade",
      cardColor: "#f4f6f8",
    },
    CORE: {
      dotColor: "#078dee",
      cardColor: "#ebfafc",
    },
    ELECTIVE: {
      dotColor: "#ff9500",
      cardColor: "#f6f1ea",
    },
  };

  // Helper to get pathway color by ID, fallback to TYPE_COLORS (same logic as Step6Summary)
  const getPathwayColor = (
    pathwayId: string | undefined,
    sequenceType: "CTE" | "CORE" | "ELECTIVE",
  ): string => {
    if (!pathwayId || !data.districtPathways) {
      return TYPE_COLORS[sequenceType].dotColor;
    }
    const pathway = data.districtPathways.find(
      (dp: any) => String(dp.id) === String(pathwayId),
    );
    // Use district pathway color if available, otherwise fallback to TYPE_COLORS
    return pathway?.color || TYPE_COLORS[sequenceType].dotColor;
  };

  // Helper to get pathway name by ID
  const getPathwayNameById = (pathwayId: string | undefined): string => {
    if (!pathwayId || !data.districtPathways) {
      return "Unknown Pathway";
    }
    const pathway = data.districtPathways.find(
      (dp: any) => String(dp.id) === String(pathwayId),
    );
    return pathway?.name || "Unknown Pathway";
  };

  // Helper to get pathway name for a sequence
  const getSequencePathwayName = (sequence: any): string => {
    // If sequence has districtPathwayIds, use the first one's pathway name
    if (sequence.districtPathwayIds && sequence.districtPathwayIds.length > 0) {
      return getPathwayNameById(sequence.districtPathwayIds[0]);
    }
    // Fallback to main pathway if available
    if (data.pathway?.id) {
      return getPathwayNameById(data.pathway.id);
    }
    return "Unknown Pathway";
  };

  // Helper to get color for a sequence based on its districtPathwayIds
  const getSequenceColor = (
    sequence: any,
    sequenceType: "CTE" | "CORE" | "ELECTIVE",
  ): string => {
    // If sequence has districtPathwayIds, use the first one's color
    if (sequence.districtPathwayIds && sequence.districtPathwayIds.length > 0) {
      return getPathwayColor(sequence.districtPathwayIds[0], sequenceType);
    }
    // Fallback to main pathway if available
    if (data.pathway?.id) {
      return getPathwayColor(data.pathway.id, sequenceType);
    }
    // Final fallback to TYPE_COLORS
    return TYPE_COLORS[sequenceType].dotColor;
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <Image src={HNLNavLogo} style={styles.logo} />
      {data.climber?.preferredID && (
        <View style={styles.headerRight}>
          <View style={styles.studentIdContainer}>
            <Image src={PreferredIdIcon} style={styles.studentIdIcon} />
            <Text style={styles.studentIdLabel}>Student ID:</Text>
            <Text style={styles.studentIdValue}>
              {data.climber.preferredID}
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderStudentInfo = () => (
    <View style={styles.studentNameSection}>
      <View style={styles.badgeContainer}>
        <Text style={styles.badgeText}>My Course Plan</Text>
      </View>
      <Text style={styles.studentName}>
        {data.climber?.firstName} {data.climber?.lastName}
      </Text>
      <View style={styles.currentSchoolContainer}>
        <Text style={styles.currentSchoolLabel}>Current School:</Text>
        <Text style={styles.currentSchool}>{data.schoolName || "N/A"}</Text>
      </View>
    </View>
  );

  const renderProgramInfo = () => {
    // Check if program has magnet or home in matchType
    const matchTypeLower = data.program?.matchType?.toLowerCase() || "";
    const hasMagnet = matchTypeLower.includes("magnet");
    const hasHome = matchTypeLower.includes("home");

    return (
      <View style={styles.infoSection}>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>HIGH SCHOOL PROGRAM:</Text>
          <View style={styles.infoValueContainer}>
            {(hasMagnet || hasHome) && (
              <View style={{ flexDirection: "row", gap: 4 }}>
                {hasMagnet && (
                  <Image src={MagnetProgramIcon} style={styles.infoIcon} />
                )}
                {hasHome && (
                  <Image src={HomeCampusIcon} style={styles.infoIcon} />
                )}
              </View>
            )}
            <Text style={styles.infoValue}>
              {data.program?.programName || "N/A"}
            </Text>
          </View>
        </View>
        {data.districtPathway && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>CTE PROGRAM:</Text>
            <Text style={styles.infoValue}>
              {data.districtPathway?.name || "N/A"}
            </Text>
          </View>
        )}
        {data.pathway && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>
              SELECTED HATS & LADDERS PATHWAY:
            </Text>
            <Text style={styles.infoValue}>{data.pathway?.name || "N/A"}</Text>
          </View>
        )}
      </View>
    );
  };

  const renderGradeHeaders = () => (
    <View style={styles.gradeHeaderRow}>
      {["Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"].map(
        (grade) => (
          <View key={grade} style={styles.gradeHeader}>
            <Text style={styles.gradeHeaderText}>{grade}</Text>
          </View>
        ),
      )}
    </View>
  );

  const renderCourseSequence = (sequence: any, color: string, tag?: string) => {
    if (!sequence.coursesByGrade) return null;

    const pathwayName = getSequencePathwayName(sequence);

    return (
      <View style={styles.sequenceContainer}>
        <View style={styles.sequenceHeader}>
          <View style={[styles.courseDot, { backgroundColor: color }]} />
          <Text style={styles.sequenceName}>{pathwayName}</Text>
          <View style={styles.sequenceLabelChip}>
            <Text style={styles.sequenceLabelChipText}>{sequence.name}</Text>
          </View>
          {tag && (
            <View style={styles.courseTag}>
              <Text style={styles.courseTagText}>{tag}</Text>
            </View>
          )}
        </View>
        <View style={styles.courseRow}>
          {sequence.coursesByGrade.map((gradeGroup: any, gIdx: number) => (
            <View key={gIdx} style={styles.courseCell}>
              {gradeGroup.courses?.length > 0 ? (
                <>
                  {gradeGroup.courses.length === 1 ? (
                    <>
                      <View style={styles.courseNameContainer}>
                        <Text style={styles.courseName}>
                          {gradeGroup.courses[0].name}
                        </Text>
                      </View>
                      <View style={styles.courseCodeContainer}>
                        <Text style={styles.courseCode}>
                          {gradeGroup.courses[0].code}
                        </Text>
                      </View>
                    </>
                  ) : (
                    <>
                      <View style={styles.courseNameContainer}>
                        <Text style={styles.courseNameWithMore}>
                          {gradeGroup.courses[0].name}
                        </Text>
                        <Text style={styles.courseMoreText}>and more</Text>
                      </View>
                      <View style={styles.courseCodeWithBadge}>
                        <Text style={styles.courseCodeText}>
                          {gradeGroup.courses[0].code}
                        </Text>
                        <View style={styles.plusBadge}>
                          <Text style={styles.plusBadgeText}>
                            +{gradeGroup.courses.length - 1}
                          </Text>
                        </View>
                      </View>
                    </>
                  )}
                </>
              ) : (
                <Text style={styles.courseName}>N/A</Text>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderCourseSection = (
    title: string,
    sequences: any[],
    sequenceType: "CTE" | "CORE" | "ELECTIVE",
  ) => {
    if (!sequences || sequences.length === 0) return null;

    return (
      <View style={styles.courseSection}>
        <Text style={styles.courseSectionTitle}>{title}</Text>
        {sequences.map((sequence, idx) => {
          const color = getSequenceColor(sequence, sequenceType);
          return (
            <View key={idx}>
              {renderCourseSequence(sequence, color, sequence.tag)}
            </View>
          );
        })}
      </View>
    );
  };

  const renderElectiveSection = (electiveSequences: any[]) => {
    if (!electiveSequences || electiveSequences.length === 0) return null;

    // Create entries for each sequence-pathway combination (matching UI behavior)
    // Each sequence can belong to multiple pathways, so create an entry for each
    const electiveEntries: Array<{
      pathwayName: string;
      sequenceLabel: string;
      dotColor: string;
    }> = [];

    electiveSequences.forEach((seq) => {
      // If no districtPathwayIds, use the main pathway from data
      const pathwayIds =
        seq.districtPathwayIds && seq.districtPathwayIds.length > 0
          ? seq.districtPathwayIds
          : data.pathway?.id
            ? [data.pathway.id]
            : ["unknown"];

      pathwayIds.forEach((pathwayId) => {
        const pathwayName = getPathwayNameById(pathwayId);
        const dotColor = getPathwayColor(pathwayId, "ELECTIVE");
        electiveEntries.push({
          pathwayName,
          sequenceLabel: seq.name,
          dotColor,
        });
      });
    });

    // Group entries by pathway name
    const groupedByPathway = new Map<string, typeof electiveEntries>();
    electiveEntries.forEach((entry) => {
      if (!groupedByPathway.has(entry.pathwayName)) {
        groupedByPathway.set(entry.pathwayName, []);
      }
      groupedByPathway.get(entry.pathwayName)!.push(entry);
    });

    return (
      <View style={styles.electivesSection}>
        <Text style={styles.electivesHeader}>Elective Courses</Text>
        <Text style={styles.electivesSubheader}>
          Possible electives you can try in High School
        </Text>
        {Array.from(groupedByPathway.entries()).map(
          ([pathwayName, entries]) => {
            // Use the color from the first entry in the pathway
            const color =
              entries.length > 0
                ? entries[0].dotColor
                : TYPE_COLORS.ELECTIVE.dotColor;
            return (
              <View key={pathwayName} style={styles.electiveCategory}>
                <View style={styles.electiveCategoryHeader}>
                  <View
                    style={[
                      styles.electiveCategoryDot,
                      { backgroundColor: color },
                    ]}
                  />
                  <Text style={styles.electiveCategoryName}>{pathwayName}</Text>
                </View>
                {entries.map((entry, idx) => (
                  <View key={idx} style={styles.electiveCard}>
                    <Text style={styles.electiveCourseName}>
                      {entry.sequenceLabel}
                    </Text>
                  </View>
                ))}
              </View>
            );
          },
        )}
      </View>
    );
  };

  // Helper to calculate pages for remaining content (max 4 rows per page)
  // Core course = 1 row, Elective = 0.5 rows
  const calculatePages = () => {
    // Page 1 shows max 3 total courses (CTE + core)
    // If CTE exists, show CTE (1) + 2 core courses = 3 total
    // If no CTE, show 3 core courses = 3 total
    const page1CoreCount = data.cteCourseSequence ? 2 : 3;
    const remainingCoreCourses =
      data.coreCourseSequences?.slice(page1CoreCount) || [];
    const electiveSequences = data.electiveCourseSequences || [];

    // Group electives by pathway name for counting (matching UI behavior)
    // Create entries for each sequence-pathway combination
    const electiveEntries: Array<{ pathwayName: string; sequence: any }> = [];
    electiveSequences.forEach((seq) => {
      const pathwayIds =
        seq.districtPathwayIds && seq.districtPathwayIds.length > 0
          ? seq.districtPathwayIds
          : data.pathway?.id
            ? [data.pathway.id]
            : ["unknown"];
      pathwayIds.forEach((pathwayId) => {
        const pathwayName = getPathwayNameById(pathwayId);
        electiveEntries.push({ pathwayName, sequence: seq });
      });
    });

    // Group entries by pathway name
    const electiveCategories: Record<string, any[]> = {};
    electiveEntries.forEach((entry) => {
      if (!electiveCategories[entry.pathwayName]) {
        electiveCategories[entry.pathwayName] = [];
      }
      electiveCategories[entry.pathwayName].push(entry.sequence);
    });

    const pages: Array<{
      coreCourses: any[];
      electiveCategories: Array<{ category: string; sequences: any[] }>;
      pageNumber: number;
    }> = [];

    let currentPage = {
      coreCourses: [] as any[],
      electiveCategories: [] as Array<{ category: string; sequences: any[] }>,
      rowCount: 0,
    };

    // Add remaining core courses (1 row each)
    remainingCoreCourses.forEach((coreCourse: any) => {
      if (currentPage.rowCount + 1 > 4) {
        pages.push({
          coreCourses: currentPage.coreCourses,
          electiveCategories: currentPage.electiveCategories,
          pageNumber: pages.length + 2, // Page 2, 3, 4...
        });
        currentPage = {
          coreCourses: [],
          electiveCategories: [],
          rowCount: 0,
        };
      }
      currentPage.coreCourses.push(coreCourse);
      currentPage.rowCount += 1;
    });

    // Add elective categories (0.5 rows each)
    Object.entries(electiveCategories).forEach(([category, sequences]) => {
      const electiveRowCount = 0.5;
      if (currentPage.rowCount + electiveRowCount > 4) {
        pages.push({
          coreCourses: currentPage.coreCourses,
          electiveCategories: currentPage.electiveCategories,
          pageNumber: pages.length + 2,
        });
        currentPage = {
          coreCourses: [],
          electiveCategories: [],
          rowCount: 0,
        };
      }
      currentPage.electiveCategories.push({ category, sequences });
      currentPage.rowCount += electiveRowCount;
    });

    // Add the last page if it has content
    if (
      currentPage.coreCourses.length > 0 ||
      currentPage.electiveCategories.length > 0
    ) {
      pages.push({
        coreCourses: currentPage.coreCourses,
        electiveCategories: currentPage.electiveCategories,
        pageNumber: pages.length + 2,
      });
    }

    return pages;
  };

  // Calculate pages once
  const pages = calculatePages();
  const totalPages = pages.length + 1; // +1 for page 1

  return (
    <Document>
      {/* Page 1 - CTE and some Core Courses */}
      <Page size="LETTER" style={styles.page}>
        <View style={styles.lightBar} />
        <View style={styles.pageContent}>
          {renderHeader()}
          {renderStudentInfo()}
          {renderProgramInfo()}
          {renderGradeHeaders()}

          {/* CTE Courses */}
          <View style={styles.courseSection}>
            <Text style={styles.courseSectionTitle}>CTE Courses</Text>
            {data.cteCourseSequence ? (
              renderCourseSequence(
                data.cteCourseSequence,
                getSequenceColor(data.cteCourseSequence, "CTE"),
                data.cteCourseSequence.tag,
              )
            ) : (
              <View style={{ marginTop: 8 }}>
                <Text style={styles.courseName}>None</Text>
              </View>
            )}
          </View>

          {/* Core Courses - Page 1 (max 3 total: CTE + core) */}
          {data.coreCourseSequences && data.coreCourseSequences.length > 0 && (
            <View style={styles.courseSection}>
              <Text style={styles.courseSectionTitle}>Core Courses</Text>
              {data.coreCourseSequences
                .slice(0, data.cteCourseSequence ? 2 : 3)
                .map((sequence: any, idx: number) =>
                  renderCourseSequence(
                    sequence,
                    getSequenceColor(sequence, "CORE"),
                    sequence.tag,
                  ),
                )}
            </View>
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>Course Plan</Text>
          <Text style={styles.footerText}>Page 1 of {totalPages}</Text>
        </View>
      </Page>

      {/* Pages 2+ - Remaining Core Courses and Electives (max 4 rows per page) */}
      {pages.map((page, pageIdx) => {
        return (
          <Page key={pageIdx} size="LETTER" style={styles.page}>
            <View style={styles.lightBar} />
            <View style={styles.pageContent}>
              {renderHeader()}
              {renderStudentInfo()}
              {renderGradeHeaders()}

              {/* Core Courses */}
              {page.coreCourses.length > 0 && (
                <View style={styles.courseSection}>
                  <Text style={styles.courseSectionTitle}>Core Courses</Text>
                  {page.coreCourses.map((sequence: any, idx: number) =>
                    renderCourseSequence(
                      sequence,
                      getSequenceColor(sequence, "CORE"),
                      sequence.tag,
                    ),
                  )}
                </View>
              )}

              {/* Elective Courses */}
              {page.electiveCategories.length > 0 && (
                <View style={styles.electivesSection}>
                  <Text style={styles.electivesHeader}>Elective Courses</Text>
                  <Text style={styles.electivesSubheader}>
                    Possible electives you can try in High School
                  </Text>
                  {page.electiveCategories.map(({ category, sequences }) => {
                    // Get color from first sequence's pathway
                    const firstSeq = sequences[0];
                    const pathwayIds =
                      firstSeq?.districtPathwayIds &&
                      firstSeq.districtPathwayIds.length > 0
                        ? firstSeq.districtPathwayIds
                        : data.pathway?.id
                          ? [data.pathway.id]
                          : ["unknown"];
                    const color =
                      pathwayIds.length > 0
                        ? getPathwayColor(pathwayIds[0], "ELECTIVE")
                        : TYPE_COLORS.ELECTIVE.dotColor;
                    return (
                      <View key={category} style={styles.electiveCategory}>
                        <View style={styles.electiveCategoryHeader}>
                          <View
                            style={[
                              styles.electiveCategoryDot,
                              { backgroundColor: color },
                            ]}
                          />
                          <Text style={styles.electiveCategoryName}>
                            {category}
                          </Text>
                        </View>
                        {sequences.map((seq, idx) => (
                          <View key={idx} style={styles.electiveCard}>
                            <Text style={styles.electiveCourseName}>
                              {seq.name}
                            </Text>
                          </View>
                        ))}
                      </View>
                    );
                  })}
                </View>
              )}
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <Text style={styles.footerText}>Course Plan</Text>
              <Text style={styles.footerText}>
                Page {page.pageNumber} of {totalPages}
              </Text>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};
